/**
 * FormAssignControl
 *
 * Toolbar select that assigns the current form to a user, to the signed-in user,
 * or clears the assignment. Rendered by `FormControl`; the write goes through
 * `assignFormToUser` from `UserContext`.
 *
 * Reads the store through a narrow snapshot — the form id and `assignedTo` only
 * — never `useFormStore()`, whose spread of `form.data` / `formVersion.data`
 * would re-render this control on every keystroke in the form. `formId` is
 * captured from the render-time snapshot before the first await, so a slow write
 * can never land on a form the user has navigated away from.
 *
 * The signed-in user is reachable only through the "Assign to me" quick action —
 * they are filtered out of the directory options — so that action's `title`
 * carries their own name and email, which is what the search filters on. The
 * name falls back to the auth display name, because a person whose directory
 * entry carries no name is the one who most needs to find themselves here.
 */

import { FC, useContext, useState } from 'react';
import { Select, Tooltip, message, theme } from 'antd';
import { CloseOutlined, CheckOutlined, QuestionOutlined, UserAddOutlined, DownOutlined } from '@ant-design/icons';
import { useSnapshot } from 'valtio';
import { UserContext } from '../../providers/UserContext';
import { formStore } from '../../store';

const { useToken } = theme;

const FormAssignControl: FC = () => {
    const { token } = useToken();
    const [isAssigning, setIsAssigning] = useState(false);
    const [messageApi, contextHolder] = message.useMessage();
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);

    const snap = useSnapshot(formStore.state);
    const formId = snap.formId;
    const assignedTo = snap.form.data?.assignedTo;
    const { user, userProfile, users, assignFormToUser } = useContext(UserContext);

    const handleChange = async (value: string) => {
        if (!formId) return;

        setIsAssigning(true);

        try {
            const userId = value === 'self' ? undefined : value === 'clear' ? null : value;

            await assignFormToUser(formId, userId);
            messageApi.success(
                value === 'clear'
                    ? 'Form unassigned'
                    : value === 'self'
                        ? 'Form assigned to you'
                        : `Form assigned to ${users.find(u => u.docId === value)?.email ?? value}`
            );
        } catch (err) {
            messageApi.error(`Failed to assign form: ${err instanceof Error ? err.message : String(err)}`);
        } finally {
            setIsAssigning(false);
        }
    };

    const currentValue = assignedTo === user?.uid
        ? 'self'
        : assignedTo || '';

    const quickActions = [];

    if (assignedTo !== user?.uid) {
        quickActions.push({
            value: 'self',
            label: (
                <span style={{ color: token.colorPrimary }}>
                    <CheckOutlined size={20}  /> Assign to me
                </span>
            ),
            title: `Assign to me ${userProfile?.name || user?.displayName || ''} ${userProfile?.email || user?.email || ''}`.trim(),
            disabled: false
        });
    }

    if (assignedTo) {
        quickActions.push({
            value: 'clear',
            label: (
                <span>
                    <CloseOutlined /> Clear assignment
                </span>
            ),
            title: '',
            disabled: false
        });
    }

    const userOptions = users
        ?.filter(u => u.docId !== user?.uid)
        .map(u => ({
            value: u.docId,
            label: (
                <>
                    <UserAddOutlined /> {u.name || u.email?.split('@')[0] || u.docId} {<span style={{ color: token.colorTextSecondary }}>{u?.email}</span>}
                </>
            ),
            title: `${u.name || ''} ${u.email || ''} ${u.docId}`.trim()
        })) ?? [];

    return (
        <Tooltip title="Assign form to...">
            {contextHolder}
            <Select
                aria-label="Form assignee"
                value={currentValue}
                onChange={handleChange}
                loading={isAssigning}
                style={{ 
                    width: 200
                }}
                prefix={currentValue === '' ? 
                    <QuestionOutlined /> : 
                    <UserAddOutlined />
                }
                suffix={<DownOutlined style={{ pointerEvents: 'none' }} />}
                styles={{
                    popup: {
                        root: {
                            minWidth: 320
                        }
                    }
                }}
                showSearch={{
                    optionFilterProp: 'title',
                    filterOption: (input, option) =>
                        option?.title?.toLowerCase().includes(input.toLowerCase()) ?? false,
                }}
                options={[
                    ...quickActions,
                    ...userOptions
                ]}
                labelInValue={false}
                placeholder={isDropdownOpen ? "Search users..." : undefined}
                onOpenChange={setIsDropdownOpen}
                labelRender={(props: { value: string | number } & Record<string, any>) => {
                    if (isDropdownOpen) return <>Search users...</>;
                    if (props.value === '') {
                        return (
                            <span style={{ pointerEvents: 'none' }}>
                                Unassigned
                            </span>
                        );
                    }
                    if (props.value === 'self') {
                        return (
                            <span style={{ pointerEvents: 'none' }}>
                                Assigned to me
                            </span>
                        );
                    }
                    return (
                        <span style={{ pointerEvents: 'none' }}>
                            Assigned to {props.title?.split(/[ @.]/)[0] || String(props.value).split(/[ @.]/)[0]}
                        </span>
                    );
                }}
            />
        </Tooltip>
    );
};

export default FormAssignControl; 